import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import variationOrdersApi from '@/api/variationOrders';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import { 
  ArrowLeft, Edit2, Save, X, Plus, Trash2, FileText, Clock, 
  CheckCircle, XCircle, AlertTriangle, Send, Play, Flag, Lock,
  DollarSign, Calendar, Building2, User, Loader2, History,
  Paperclip, MessageSquare, Search, Link
} from 'lucide-react';

// Status badge colors
const getStatusBadge = (status) => {
  const styles = {
    'Draft': 'bg-slate-100 text-slate-700 border-slate-200',
    'RFQ Issued': 'bg-blue-100 text-blue-700 border-blue-200',
    'Quote Received': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Under Review': 'bg-amber-100 text-amber-700 border-amber-200',
    'Clarification': 'bg-orange-100 text-orange-700 border-orange-200',
    'Negotiation': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'Recommended': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'Technically Approved': 'bg-teal-100 text-teal-700 border-teal-200',
    'Commercially Approved': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'VO Issued': 'bg-green-100 text-green-700 border-green-200',
    'In Progress': 'bg-purple-100 text-purple-700 border-purple-200',
    'Implemented': 'bg-violet-100 text-violet-700 border-violet-200',
    'Valued/Assessed': 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
    'Certified': 'bg-pink-100 text-pink-700 border-pink-200',
    'Paid': 'bg-lime-100 text-lime-700 border-lime-200',
    'Closed': 'bg-gray-100 text-gray-700 border-gray-200',
    'Rejected': 'bg-red-100 text-red-700 border-red-200',
    'Disputed': 'bg-rose-100 text-rose-700 border-rose-200',
    'On Hold': 'bg-zinc-100 text-zinc-700 border-zinc-200',
    'Cancelled': 'bg-neutral-100 text-neutral-700 border-neutral-200',
  };
  return styles[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(amount);
};

// ============== RFQs and Quotations Tab Component ==============
function RFQsAndQuotationsTab({ voId, vo, onRefresh, formatCurrency }) {
  const [rfqs, setRfqs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [commercialSummary, setCommercialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createRfqOpen, setCreateRfqOpen] = useState(false);
  const [createQuoteOpen, setCreateQuoteOpen] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  
  // RFQ Form
  const [rfqForm, setRfqForm] = useState({
    issued_to_company: '',
    issued_to_contact: '',
    issued_to_email: '',
    scope_description: vo?.description || '',
    response_due_date: '',
    instructions: '',
    line_items: []
  });
  
  // Quotation Form
  const [quoteForm, setQuoteForm] = useState({
    rfq_id: '',
    submitted_by_company: '',
    submitted_by_contact: '',
    submitted_by_email: '',
    line_items: [],
    subtotal: 0,
    preliminaries: 0,
    preliminaries_percentage: 0,
    oh_and_p: 0,
    oh_and_p_percentage: 10,
    vat: 0,
    vat_percentage: 15,
    total: 0,
    time_impact_days: 0,
    time_impact_notes: '',
    validity_period_days: 30,
    exclusions: '',
    assumptions: ''
  });
  
  // Assessment Form
  const [assessmentForm, setAssessmentForm] = useState({
    assessed_line_items: [],
    assessed_subtotal: 0,
    assessed_preliminaries: 0,
    assessed_oh_and_p: 0,
    assessed_vat: 0,
    assessed_total: 0,
    assessed_time_impact_days: 0,
    time_impact_notes: '',
    assessment_notes: '',
    recommendations: ''
  });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [rfqsRes, quotesRes, summaryRes] = await Promise.all([
          variationOrdersApi.listRfqs(voId),
          variationOrdersApi.listQuotations(voId),
          variationOrdersApi.getCommercialSummary(voId)
        ]);
        setRfqs(rfqsRes.data.rfqs || []);
        setQuotations(quotesRes.data.quotations || []);
        setCommercialSummary(summaryRes.data);
      } catch (error) {
        console.error('Failed to load RFQs/Quotations:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [voId]);

  // Create RFQ
  const handleCreateRfq = async () => {
    if (!rfqForm.issued_to_company || !rfqForm.scope_description) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await variationOrdersApi.createRfq(voId, rfqForm);
      toast.success('RFQ created and issued');
      setCreateRfqOpen(false);
      setRfqForm({ issued_to_company: '', issued_to_contact: '', issued_to_email: '', scope_description: vo?.description || '', response_due_date: '', instructions: '', line_items: [] });
      onRefresh();
      // Reload RFQs
      const res = await variationOrdersApi.listRfqs(voId);
      setRfqs(res.data.rfqs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create RFQ');
    }
  };

  // Create Quotation
  const handleCreateQuote = async () => {
    if (!quoteForm.submitted_by_company) {
      toast.error('Company name is required');
      return;
    }
    try {
      await variationOrdersApi.createQuotation(voId, quoteForm);
      toast.success('Quotation submitted');
      setCreateQuoteOpen(false);
      setQuoteForm({ rfq_id: '', submitted_by_company: '', submitted_by_contact: '', submitted_by_email: '', line_items: [], subtotal: 0, preliminaries: 0, preliminaries_percentage: 0, oh_and_p: 0, oh_and_p_percentage: 10, vat: 0, vat_percentage: 15, total: 0, time_impact_days: 0, time_impact_notes: '', validity_period_days: 30, exclusions: '', assumptions: '' });
      onRefresh();
      const res = await variationOrdersApi.listQuotations(voId);
      setQuotations(res.data.quotations || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quotation');
    }
  };

  // Accept Quotation
  const handleAcceptQuote = async (quotationId) => {
    if (!confirm('Accept this quotation? Other quotations will be superseded.')) return;
    try {
      await variationOrdersApi.acceptQuotation(voId, quotationId);
      toast.success('Quotation accepted');
      onRefresh();
      const res = await variationOrdersApi.listQuotations(voId);
      setQuotations(res.data.quotations || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quotation');
    }
  };

  // Reject Quotation
  const handleRejectQuote = async (quotationId) => {
    const reason = prompt('Enter reason for rejection (optional):');
    try {
      await variationOrdersApi.rejectQuotation(voId, quotationId, reason);
      toast.success('Quotation rejected');
      const res = await variationOrdersApi.listQuotations(voId);
      setQuotations(res.data.quotations || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject quotation');
    }
  };

  // Create Assessment
  const handleCreateAssessment = async () => {
    if (!selectedQuotation) return;
    try {
      await variationOrdersApi.createAssessment(voId, selectedQuotation.id, assessmentForm);
      toast.success('QS Assessment completed');
      setAssessmentOpen(false);
      setSelectedQuotation(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assessment');
    }
  };

  // Calculate quotation totals
  const calculateQuoteTotals = (form) => {
    const subtotal = form.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const preliminaries = form.preliminaries_percentage ? subtotal * (form.preliminaries_percentage / 100) : form.preliminaries || 0;
    const oh_and_p = form.oh_and_p_percentage ? subtotal * (form.oh_and_p_percentage / 100) : form.oh_and_p || 0;
    const subtotalWithMarkups = subtotal + preliminaries + oh_and_p;
    const vat = form.vat_percentage ? subtotalWithMarkups * (form.vat_percentage / 100) : 0;
    const total = subtotalWithMarkups + vat;
    return { subtotal, preliminaries, oh_and_p, vat, total };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Commercial Summary Card */}
      {commercialSummary && (
        <Card className="rounded-sm bg-gradient-to-r from-slate-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-lg">Commercial Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Quoted Total</p>
                <p className="text-xl font-bold text-amber-600">
                  {formatCurrency(commercialSummary.quoted_total || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Assessed Total</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(commercialSummary.assessed_total || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Approved Total</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(commercialSummary.approved_total || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Variance</p>
                <p className={`text-xl font-bold ${(commercialSummary.variance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(commercialSummary.variance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* RFQs Section */}
      <Card className="rounded-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="h-5 w-5" />
            Requests for Quotation
          </CardTitle>
          {['Draft', 'RFQ Issued'].includes(vo?.status) && (
            <Button size="sm" onClick={() => setCreateRfqOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Issue RFQ
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {rfqs.length > 0 ? (
            <div className="space-y-3">
              {rfqs.map((rfq) => (
                <div key={rfq.id} className="border rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{rfq.rfq_number}</span>
                      <Badge className={
                        rfq.status === 'Issued' ? 'bg-blue-100 text-blue-700' :
                        rfq.status === 'Responded' ? 'bg-green-100 text-green-700' :
                        rfq.status === 'Closed' ? 'bg-slate-100 text-slate-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {rfq.status}
                      </Badge>
                    </div>
                    <span className="text-sm text-slate-500 font-mono">
                      {new Date(rfq.issued_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm font-medium">{rfq.issued_to_company}</p>
                  {rfq.issued_to_contact && (
                    <p className="text-xs text-slate-500">{rfq.issued_to_contact} • {rfq.issued_to_email}</p>
                  )}
                  {rfq.response_due_date && (
                    <p className="text-xs text-slate-500 mt-1">
                      Due: {new Date(rfq.response_due_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Send className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No RFQs issued yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotations Section */}
      <Card className="rounded-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Quotations
          </CardTitle>
          <Button size="sm" onClick={() => setCreateQuoteOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Submit Quotation
          </Button>
        </CardHeader>
        <CardContent>
          {quotations.length > 0 ? (
            <div className="space-y-3">
              {quotations.map((quote) => (
                <div key={quote.id} className="border rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{quote.quotation_number}</span>
                      <Badge className={
                        quote.status === 'Submitted' ? 'bg-blue-100 text-blue-700' :
                        quote.status === 'Under Review' ? 'bg-amber-100 text-amber-700' :
                        quote.status === 'Accepted' ? 'bg-green-100 text-green-700' :
                        quote.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        quote.status === 'Superseded' ? 'bg-slate-100 text-slate-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {quote.status}
                      </Badge>
                      {quote.revision > 1 && (
                        <Badge variant="outline" className="text-xs">Rev {quote.revision}</Badge>
                      )}
                    </div>
                    <span className="text-lg font-bold">{formatCurrency(quote.total)}</span>
                  </div>
                  <p className="text-sm font-medium">{quote.submitted_by_company}</p>
                  {quote.time_impact_days > 0 && (
                    <p className="text-xs text-purple-600 mt-1">
                      Time Impact: {quote.time_impact_days} days
                    </p>
                  )}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t">
                    <span className="text-xs text-slate-500">
                      Submitted: {new Date(quote.submitted_at).toLocaleDateString()}
                      {quote.valid_until && ` • Valid until: ${new Date(quote.valid_until).toLocaleDateString()}`}
                    </span>
                    <div className="flex gap-2">
                      {quote.status === 'Submitted' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => {
                            setSelectedQuotation(quote);
                            setAssessmentForm({
                              assessed_line_items: quote.line_items || [],
                              assessed_subtotal: quote.subtotal || 0,
                              assessed_preliminaries: quote.preliminaries || 0,
                              assessed_oh_and_p: quote.oh_and_p || 0,
                              assessed_vat: quote.vat || 0,
                              assessed_total: quote.total || 0,
                              assessed_time_impact_days: quote.time_impact_days || 0,
                              time_impact_notes: '',
                              assessment_notes: '',
                              recommendations: ''
                            });
                            setAssessmentOpen(true);
                          }}>
                            <DollarSign className="h-3 w-3 mr-1" />
                            Assess
                          </Button>
                          <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleAcceptQuote(quote.id)}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Accept
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectQuote(quote.id)}>
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No quotations received yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create RFQ Dialog */}
      <Dialog open={createRfqOpen} onOpenChange={setCreateRfqOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Issue Request for Quotation</DialogTitle>
            <DialogDescription>Request a quotation from the contractor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={rfqForm.issued_to_company}
                  onChange={(e) => setRfqForm({ ...rfqForm, issued_to_company: e.target.value })}
                  placeholder="Contractor company"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={rfqForm.issued_to_contact}
                  onChange={(e) => setRfqForm({ ...rfqForm, issued_to_contact: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={rfqForm.issued_to_email}
                  onChange={(e) => setRfqForm({ ...rfqForm, issued_to_email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Response Due Date</Label>
                <Input
                  type="date"
                  value={rfqForm.response_due_date}
                  onChange={(e) => setRfqForm({ ...rfqForm, response_due_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Scope Description *</Label>
              <Textarea
                value={rfqForm.scope_description}
                onChange={(e) => setRfqForm({ ...rfqForm, scope_description: e.target.value })}
                rows={4}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Instructions</Label>
              <Textarea
                value={rfqForm.instructions}
                onChange={(e) => setRfqForm({ ...rfqForm, instructions: e.target.value })}
                rows={2}
                className="mt-1"
                placeholder="Any special instructions for the quotation..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRfqOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRfq}>
              <Send className="h-4 w-4 mr-2" />
              Issue RFQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Quotation Dialog */}
      <Dialog open={createQuoteOpen} onOpenChange={setCreateQuoteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Submit Quotation</DialogTitle>
            <DialogDescription>Enter contractor quotation details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {rfqs.length > 0 && (
              <div>
                <Label>In Response to RFQ (Optional)</Label>
                <Select
                  value={quoteForm.rfq_id || 'none'}
                  onValueChange={(v) => setQuoteForm({ ...quoteForm, rfq_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select RFQ..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific RFQ</SelectItem>
                    {rfqs.map((rfq) => (
                      <SelectItem key={rfq.id} value={rfq.id}>
                        {rfq.rfq_number} - {rfq.issued_to_company}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={quoteForm.submitted_by_company}
                  onChange={(e) => setQuoteForm({ ...quoteForm, submitted_by_company: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={quoteForm.submitted_by_contact}
                  onChange={(e) => setQuoteForm({ ...quoteForm, submitted_by_contact: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={quoteForm.submitted_by_email}
                  onChange={(e) => setQuoteForm({ ...quoteForm, submitted_by_email: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subtotal</Label>
                <Input
                  type="number"
                  value={quoteForm.subtotal}
                  onChange={(e) => {
                    const subtotal = parseFloat(e.target.value) || 0;
                    const newForm = { ...quoteForm, subtotal };
                    const totals = calculateQuoteTotals(newForm);
                    setQuoteForm({ ...newForm, ...totals });
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Preliminaries (%)</Label>
                <Input
                  type="number"
                  value={quoteForm.preliminaries_percentage}
                  onChange={(e) => {
                    const newForm = { ...quoteForm, preliminaries_percentage: parseFloat(e.target.value) || 0 };
                    const totals = calculateQuoteTotals(newForm);
                    setQuoteForm({ ...newForm, ...totals });
                  }}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>OH&P (%)</Label>
                <Input
                  type="number"
                  value={quoteForm.oh_and_p_percentage}
                  onChange={(e) => {
                    const newForm = { ...quoteForm, oh_and_p_percentage: parseFloat(e.target.value) || 0 };
                    const totals = calculateQuoteTotals(newForm);
                    setQuoteForm({ ...newForm, ...totals });
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>VAT (%)</Label>
                <Input
                  type="number"
                  value={quoteForm.vat_percentage}
                  onChange={(e) => {
                    const newForm = { ...quoteForm, vat_percentage: parseFloat(e.target.value) || 0 };
                    const totals = calculateQuoteTotals(newForm);
                    setQuoteForm({ ...newForm, ...totals });
                  }}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-mono">{formatCurrency(quoteForm.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Preliminaries:</span>
                  <span className="font-mono">{formatCurrency(quoteForm.preliminaries)}</span>
                </div>
                <div className="flex justify-between">
                  <span>OH&P:</span>
                  <span className="font-mono">{formatCurrency(quoteForm.oh_and_p)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT:</span>
                  <span className="font-mono">{formatCurrency(quoteForm.vat)}</span>
                </div>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-bold">
                <span>TOTAL:</span>
                <span className="font-mono text-lg">{formatCurrency(quoteForm.total)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Time Impact (Days)</Label>
                <Input
                  type="number"
                  value={quoteForm.time_impact_days}
                  onChange={(e) => setQuoteForm({ ...quoteForm, time_impact_days: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Validity Period (Days)</Label>
                <Input
                  type="number"
                  value={quoteForm.validity_period_days}
                  onChange={(e) => setQuoteForm({ ...quoteForm, validity_period_days: parseInt(e.target.value) || 30 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Exclusions</Label>
              <Textarea
                value={quoteForm.exclusions}
                onChange={(e) => setQuoteForm({ ...quoteForm, exclusions: e.target.value })}
                rows={2}
                className="mt-1"
                placeholder="Items excluded from this quotation..."
              />
            </div>
            <div>
              <Label>Assumptions</Label>
              <Textarea
                value={quoteForm.assumptions}
                onChange={(e) => setQuoteForm({ ...quoteForm, assumptions: e.target.value })}
                rows={2}
                className="mt-1"
                placeholder="Assumptions made in this quotation..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateQuoteOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateQuote}>
              <FileText className="h-4 w-4 mr-2" />
              Submit Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QS Assessment Dialog */}
      <Dialog open={assessmentOpen} onOpenChange={setAssessmentOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>QS Assessment</DialogTitle>
            <DialogDescription>
              Assess quotation {selectedQuotation?.quotation_number} from {selectedQuotation?.submitted_by_company}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="bg-amber-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Quoted Total: {formatCurrency(selectedQuotation?.total || 0)}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assessed Subtotal</Label>
                <Input
                  type="number"
                  value={assessmentForm.assessed_subtotal}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_subtotal: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Assessed Preliminaries</Label>
                <Input
                  type="number"
                  value={assessmentForm.assessed_preliminaries}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_preliminaries: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assessed OH&P</Label>
                <Input
                  type="number"
                  value={assessmentForm.assessed_oh_and_p}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_oh_and_p: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Assessed VAT</Label>
                <Input
                  type="number"
                  value={assessmentForm.assessed_vat}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_vat: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Assessed Total</Label>
              <Input
                type="number"
                value={assessmentForm.assessed_total}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_total: parseFloat(e.target.value) || 0 })}
                className="mt-1"
              />
              {assessmentForm.assessed_total > 0 && selectedQuotation?.total > 0 && (
                <p className="text-sm mt-1 text-slate-600">
                  Variance: {formatCurrency((selectedQuotation.total || 0) - assessmentForm.assessed_total)} 
                  ({(((selectedQuotation.total || 0) - assessmentForm.assessed_total) / (selectedQuotation.total || 1) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
            
            <Separator />
            
            <div>
              <Label>Assessed Time Impact (Days)</Label>
              <Input
                type="number"
                value={assessmentForm.assessed_time_impact_days}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, assessed_time_impact_days: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Assessment Notes</Label>
              <Textarea
                value={assessmentForm.assessment_notes}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_notes: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="Notes on the assessment methodology and findings..."
              />
            </div>
            <div>
              <Label>Recommendations</Label>
              <Textarea
                value={assessmentForm.recommendations}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, recommendations: e.target.value })}
                rows={2}
                className="mt-1"
                placeholder="Recommendations for approval/negotiation..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssessmentOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateAssessment}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Payments Tab Component ==============
function PaymentsTab({ voId, vo, onRefresh, formatCurrency }) {
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [addSecurityOpen, setAddSecurityOpen] = useState(false);
  const [linkCertificateOpen, setLinkCertificateOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  
  // Payment Plan Form
  const [planForm, setPlanForm] = useState({
    payment_route: 'Include in Payment Certificate',
    total_amount: vo?.approved_total || 0,
    advance_amount: '',
    advance_percentage: '',
    advance_conditions: '',
    security_required: false,
    security_amount: '',
    repayment_method: 'Percentage per Certificate',
    repayment_percentage: 10,
    notes: ''
  });
  
  // Security Form
  const [securityForm, setSecurityForm] = useState({
    security_type: 'Advance Payment Guarantee',
    security_number: '',
    issuer: '',
    amount: '',
    expiry_date: '',
    notes: ''
  });
  
  // Certificate Link Form
  const [certForm, setCertForm] = useState({
    certificate_id: '',
    certificate_number: '',
    certificate_date: '',
    amount_included: '',
    is_advance_deduction: false,
    advance_deduction_amount: ''
  });
  
  // Payment Form
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: '',
    payment_reference: '',
    notes: ''
  });

  const PAYMENT_ROUTES = [
    { value: 'Include in Payment Certificate', label: 'Include in Payment Certificate' },
    { value: 'Separate Payment', label: 'Separate Payment' },
    { value: 'Advance Payment / Deposit', label: 'Advance Payment / Deposit' }
  ];
  
  const REPAYMENT_METHODS = [
    { value: 'Percentage per Certificate', label: 'Percentage per Certificate' },
    { value: 'Fixed Amount per Certificate', label: 'Fixed Amount per Certificate' },
    { value: 'Milestone Based', label: 'Milestone Based' },
    { value: 'Lump Sum at Final', label: 'Lump Sum at Final' }
  ];
  
  const SECURITY_TYPES = [
    'Advance Payment Guarantee',
    'Bank Guarantee',
    'Performance Bond',
    'Retention Guarantee',
    'Insurance',
    'Other'
  ];

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const summaryRes = await variationOrdersApi.getPaymentSummary(voId);
        setPaymentSummary(summaryRes.data);
        
        if (summaryRes.data.has_payment_plan) {
          const planRes = await variationOrdersApi.getPaymentPlan(voId);
          setPaymentPlan(planRes.data.payment_plan);
        }
      } catch (error) {
        console.error('Failed to load payment data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [voId]);

  // Create payment plan
  const handleCreatePlan = async () => {
    try {
      const data = {
        payment_route: planForm.payment_route,
        total_amount: parseFloat(planForm.total_amount) || 0,
        advance_amount: planForm.advance_amount ? parseFloat(planForm.advance_amount) : null,
        advance_percentage: planForm.advance_percentage ? parseFloat(planForm.advance_percentage) : null,
        advance_conditions: planForm.advance_conditions || null,
        security_required: planForm.security_required,
        security_amount: planForm.security_amount ? parseFloat(planForm.security_amount) : null,
        repayment_method: planForm.payment_route === 'Advance Payment / Deposit' ? planForm.repayment_method : null,
        repayment_percentage: planForm.repayment_percentage ? parseFloat(planForm.repayment_percentage) : null,
        notes: planForm.notes || null
      };
      
      await variationOrdersApi.createPaymentPlan(voId, data);
      toast.success('Payment plan created');
      setCreatePlanOpen(false);
      
      // Refresh
      const summaryRes = await variationOrdersApi.getPaymentSummary(voId);
      setPaymentSummary(summaryRes.data);
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment plan');
    }
  };

  // Add security
  const handleAddSecurity = async () => {
    if (!securityForm.issuer || !securityForm.amount) {
      toast.error('Issuer and amount are required');
      return;
    }
    try {
      await variationOrdersApi.addSecurity(voId, {
        security_type: securityForm.security_type,
        security_number: securityForm.security_number || null,
        issuer: securityForm.issuer,
        amount: parseFloat(securityForm.amount),
        expiry_date: securityForm.expiry_date || null,
        notes: securityForm.notes || null
      });
      toast.success('Security added');
      setAddSecurityOpen(false);
      setSecurityForm({ security_type: 'Advance Payment Guarantee', security_number: '', issuer: '', amount: '', expiry_date: '', notes: '' });
      
      // Refresh
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add security');
    }
  };

  // Release security
  const handleReleaseSecurity = async (securityId) => {
    if (!confirm('Release this security?')) return;
    try {
      await variationOrdersApi.releaseSecurity(voId, securityId);
      toast.success('Security released');
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release security');
    }
  };

  // Generate repayment schedule
  const handleGenerateSchedule = async () => {
    try {
      await variationOrdersApi.generateRepaymentSchedule(voId, 10);
      toast.success('Repayment schedule generated');
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate schedule');
    }
  };

  // Mark repayment as deducted
  const handleDeductRepayment = async (repaymentId, amount) => {
    try {
      await variationOrdersApi.markRepaymentDeducted(voId, repaymentId, amount);
      toast.success('Repayment marked as deducted');
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deduct repayment');
    }
  };

  // Record payment
  const handleRecordPayment = async () => {
    if (!paymentForm.amount_paid) {
      toast.error('Amount is required');
      return;
    }
    try {
      await variationOrdersApi.markPaymentReceived(voId, {
        amount_paid: parseFloat(paymentForm.amount_paid),
        payment_reference: paymentForm.payment_reference || null,
        notes: paymentForm.notes || null
      });
      toast.success('Payment recorded');
      setRecordPaymentOpen(false);
      setPaymentForm({ amount_paid: '', payment_reference: '', notes: '' });
      
      // Refresh
      const summaryRes = await variationOrdersApi.getPaymentSummary(voId);
      setPaymentSummary(summaryRes.data);
      const planRes = await variationOrdersApi.getPaymentPlan(voId);
      setPaymentPlan(planRes.data.payment_plan);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // No payment plan yet
  if (!paymentSummary?.has_payment_plan) {
    return (
      <div className="space-y-6">
        <Card className="rounded-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Payment Plan</h3>
            <p className="text-slate-500 mb-4">Create a payment plan to track payments, advances, and certificates</p>
            <Button onClick={() => {
              setPlanForm({ ...planForm, total_amount: vo?.approved_total || 0 });
              setCreatePlanOpen(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Payment Plan
            </Button>
          </CardContent>
        </Card>
        
        {/* Create Plan Dialog */}
        <Dialog open={createPlanOpen} onOpenChange={setCreatePlanOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Payment Plan</DialogTitle>
              <DialogDescription>Set up payment route and terms for this variation order</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Payment Route</Label>
                <Select value={planForm.payment_route} onValueChange={(v) => setPlanForm({ ...planForm, payment_route: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_ROUTES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Total Amount</Label>
                <Input
                  type="number"
                  value={planForm.total_amount}
                  onChange={(e) => setPlanForm({ ...planForm, total_amount: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {planForm.payment_route === 'Advance Payment / Deposit' && (
                <>
                  <Separator />
                  <p className="text-sm font-medium text-slate-700">Advance Payment Details</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Advance Amount</Label>
                      <Input
                        type="number"
                        value={planForm.advance_amount}
                        onChange={(e) => setPlanForm({ ...planForm, advance_amount: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Or Advance Percentage (%)</Label>
                      <Input
                        type="number"
                        value={planForm.advance_percentage}
                        onChange={(e) => setPlanForm({ ...planForm, advance_percentage: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Checkbox
                      id="security_required"
                      checked={planForm.security_required}
                      onCheckedChange={(checked) => setPlanForm({ ...planForm, security_required: checked })}
                    />
                    <div>
                      <label htmlFor="security_required" className="text-sm font-medium text-amber-800 cursor-pointer">
                        Security/Guarantee Required
                      </label>
                      <p className="text-xs text-amber-600">Advance payment guarantee or bank guarantee</p>
                    </div>
                  </div>
                  
                  {planForm.security_required && (
                    <div>
                      <Label>Security Amount</Label>
                      <Input
                        type="number"
                        value={planForm.security_amount}
                        onChange={(e) => setPlanForm({ ...planForm, security_amount: e.target.value })}
                        placeholder="Same as advance if not specified"
                        className="mt-1"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label>Repayment Method</Label>
                    <Select value={planForm.repayment_method} onValueChange={(v) => setPlanForm({ ...planForm, repayment_method: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPAYMENT_METHODS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {planForm.repayment_method === 'Percentage per Certificate' && (
                    <div>
                      <Label>Repayment Percentage per Certificate (%)</Label>
                      <Input
                        type="number"
                        value={planForm.repayment_percentage}
                        onChange={(e) => setPlanForm({ ...planForm, repayment_percentage: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label>Conditions Precedent</Label>
                    <Textarea
                      value={planForm.advance_conditions}
                      onChange={(e) => setPlanForm({ ...planForm, advance_conditions: e.target.value })}
                      placeholder="Conditions that must be met before advance is paid..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={planForm.notes}
                  onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatePlanOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePlan}>
                <DollarSign className="h-4 w-4 mr-2" />
                Create Payment Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Has payment plan
  const summary = paymentSummary.financial_summary || {};
  const plan = paymentSummary.payment_plan || {};

  return (
    <div className="space-y-6">
      {/* Financial Summary Card */}
      <Card className="rounded-sm bg-gradient-to-r from-green-50 to-slate-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase">Total VO Value</p>
              <p className="text-xl font-bold">{formatCurrency(summary.total_vo_value || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Certified to Date</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(summary.certified_to_date || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Paid to Date</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(summary.paid_to_date || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Outstanding</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(summary.outstanding_payment || 0)}</p>
            </div>
          </div>
          
          {plan.advance_amount > 0 && (
            <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Advance Issued</p>
                <p className="font-bold text-purple-600">{formatCurrency(summary.advance_issued || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Advance Recovered</p>
                <p className="font-bold text-teal-600">{formatCurrency(summary.advance_recovered || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Advance Outstanding</p>
                <p className="font-bold text-red-600">{formatCurrency(summary.advance_outstanding || 0)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Plan Details */}
      <Card className="rounded-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Payment Plan</CardTitle>
            <div className="text-sm text-slate-500 mt-1 flex items-center gap-1">
              Route: {plan.payment_route} • Status: <Badge variant="outline">{plan.status}</Badge>
            </div>
          </div>
          <Button size="sm" onClick={() => setRecordPaymentOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Record Payment
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Total Amount</p>
              <p className="font-mono font-medium">{formatCurrency(plan.total_amount)}</p>
            </div>
            {plan.advance_amount > 0 && (
              <>
                <div>
                  <p className="text-slate-500">Advance Amount</p>
                  <p className="font-mono font-medium">{formatCurrency(plan.advance_amount)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Outstanding Advance</p>
                  <p className="font-mono font-medium text-red-600">{formatCurrency(plan.outstanding_advance)}</p>
                </div>
              </>
            )}
            <div>
              <p className="text-slate-500">Repayment Method</p>
              <p className="font-medium">{plan.repayment_method || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Securities */}
      {plan.security_required && (
        <Card className="rounded-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Securities & Guarantees
              <Badge variant="secondary">{plan.securities_count}</Badge>
            </CardTitle>
            <Button size="sm" onClick={() => setAddSecurityOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Security
            </Button>
          </CardHeader>
          <CardContent>
            {paymentPlan?.securities?.length > 0 ? (
              <div className="space-y-3">
                {paymentPlan.securities.map((sec) => (
                  <div key={sec.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={sec.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {sec.status}
                        </Badge>
                        <span className="font-medium">{sec.security_type}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {sec.issuer} {sec.security_number && `• ${sec.security_number}`}
                      </p>
                      <p className="text-sm font-mono font-bold mt-1">{formatCurrency(sec.amount)}</p>
                    </div>
                    {sec.status === 'Active' && (
                      <Button size="sm" variant="outline" onClick={() => handleReleaseSecurity(sec.id)}>
                        Release
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Lock className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No securities added yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Repayment Schedule */}
      {plan.advance_amount > 0 && (
        <Card className="rounded-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Repayment Schedule
              <Badge variant="secondary">{plan.repayment_schedule_count} entries</Badge>
            </CardTitle>
            {paymentPlan?.repayment_schedule?.length === 0 && (
              <Button size="sm" variant="outline" onClick={handleGenerateSchedule}>
                Generate Schedule
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {paymentPlan?.repayment_schedule?.length > 0 ? (
              <div className="space-y-2">
                {paymentPlan.repayment_schedule.map((rep) => (
                  <div key={rep.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Badge className={rep.status === 'Deducted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {rep.status}
                      </Badge>
                      <span>{rep.certificate_number || rep.milestone || 'Payment'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold">{formatCurrency(rep.amount)}</span>
                      {rep.status === 'Pending' && (
                        <Button size="sm" variant="outline" onClick={() => handleDeductRepayment(rep.id, rep.amount)}>
                          Deduct
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Calendar className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No repayment schedule. Click "Generate Schedule" to create one.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Security Dialog */}
      <Dialog open={addSecurityOpen} onOpenChange={setAddSecurityOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Security</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Security Type</Label>
              <Select value={securityForm.security_type} onValueChange={(v) => setSecurityForm({ ...securityForm, security_type: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECURITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issuer (Bank/Insurer) *</Label>
                <Input
                  value={securityForm.issuer}
                  onChange={(e) => setSecurityForm({ ...securityForm, issuer: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={securityForm.security_number}
                  onChange={(e) => setSecurityForm({ ...securityForm, security_number: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={securityForm.amount}
                  onChange={(e) => setSecurityForm({ ...securityForm, amount: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={securityForm.expiry_date}
                  onChange={(e) => setSecurityForm({ ...securityForm, expiry_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSecurityOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSecurity}>Add Security</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onOpenChange={setRecordPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount Paid *</Label>
              <Input
                type="number"
                value={paymentForm.amount_paid}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Payment Reference</Label>
              <Input
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                placeholder="Bank reference, cheque number, etc."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordPaymentOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>
              <DollarSign className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Linked Items Tab Component ==============
function LinkedItemsTab({ voId, vo, onRefresh }) {
  const [linkedItems, setLinkedItems] = useState([]);
  const [linkableItems, setLinkableItems] = useState([]);
  const [timeImpact, setTimeImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [timeImpactDialogOpen, setTimeImpactDialogOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState('delay_event');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Time Impact Form
  const [timeImpactForm, setTimeImpactForm] = useState({
    impact_type: 'None',
    claimed_days: '',
    assessed_days: '',
    approved_days: '',
    delay_event_id: '',
    notes: ''
  });

  const ITEM_TYPES = [
    { value: 'delay_event', label: 'Delay Events', icon: Clock },
    { value: 'rfi', label: 'RFIs', icon: FileText },
    { value: 'site_instruction', label: 'Site Instructions', icon: FileText },
    { value: 'shop_drawing', label: 'Shop Drawings', icon: FileText },
    { value: 'meeting_minutes', label: 'Meeting Minutes', icon: FileText }
  ];

  // Load linked items
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [linkedRes, timeRes] = await Promise.all([
          variationOrdersApi.getLinkedItems(voId),
          variationOrdersApi.getTimeImpactDetails(voId)
        ]);
        setLinkedItems(linkedRes.data.linked_items || []);
        setTimeImpact(timeRes.data);
        
        // Update time impact form
        const ti = timeRes.data.time_impact || {};
        setTimeImpactForm({
          impact_type: ti.impact_type || 'None',
          claimed_days: ti.claimed_days?.toString() || '',
          assessed_days: ti.assessed_days?.toString() || '',
          approved_days: ti.approved_days?.toString() || '',
          delay_event_id: ti.delay_event_id || '',
          notes: ti.notes || ''
        });
      } catch (error) {
        console.error('Failed to load linked items:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [voId]);

  // Search for linkable items
  const handleSearchLinkable = async () => {
    try {
      const res = await variationOrdersApi.getLinkableItems(voId, selectedItemType, searchQuery);
      setLinkableItems(res.data.items || []);
    } catch (error) {
      console.error('Failed to search linkable items:', error);
      toast.error('Failed to search items');
    }
  };

  useEffect(() => {
    if (linkDialogOpen) {
      handleSearchLinkable();
    }
  }, [linkDialogOpen, selectedItemType]);

  // Link item
  const handleLinkItem = async (itemId) => {
    try {
      await variationOrdersApi.addLinkedItem(voId, {
        item_id: itemId,
        item_type: selectedItemType,
        link_notes: ''
      });
      toast.success('Item linked successfully');
      // Refresh
      const res = await variationOrdersApi.getLinkedItems(voId);
      setLinkedItems(res.data.linked_items || []);
      handleSearchLinkable(); // Refresh search results
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link item');
    }
  };

  // Unlink item
  const handleUnlinkItem = async (itemId) => {
    if (!confirm('Remove this linked item?')) return;
    try {
      await variationOrdersApi.removeLinkedItem(voId, itemId);
      toast.success('Item unlinked');
      const res = await variationOrdersApi.getLinkedItems(voId);
      setLinkedItems(res.data.linked_items || []);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink item');
    }
  };

  // Save time impact
  const handleSaveTimeImpact = async () => {
    try {
      await variationOrdersApi.updateTimeImpact(voId, {
        impact_type: timeImpactForm.impact_type,
        claimed_days: timeImpactForm.claimed_days ? parseInt(timeImpactForm.claimed_days) : null,
        assessed_days: timeImpactForm.assessed_days ? parseInt(timeImpactForm.assessed_days) : null,
        approved_days: timeImpactForm.approved_days ? parseInt(timeImpactForm.approved_days) : null,
        delay_event_id: timeImpactForm.delay_event_id || null,
        notes: timeImpactForm.notes || null
      });
      toast.success('Time impact updated');
      setTimeImpactDialogOpen(false);
      // Refresh
      const res = await variationOrdersApi.getTimeImpactDetails(voId);
      setTimeImpact(res.data);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update time impact');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Impact Card */}
      <Card className="rounded-sm bg-gradient-to-r from-purple-50 to-slate-50">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Time Impact
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setTimeImpactDialogOpen(true)}>
            <Edit2 className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase">Impact Type</p>
              <p className="font-medium">
                {timeImpact?.time_impact?.impact_type || 'None'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Claimed Days</p>
              <p className="text-xl font-bold text-amber-600">
                {timeImpact?.time_impact?.claimed_days || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Assessed Days</p>
              <p className="text-xl font-bold text-blue-600">
                {timeImpact?.time_impact?.assessed_days || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Approved Days</p>
              <p className="text-xl font-bold text-green-600">
                {timeImpact?.time_impact?.approved_days || 0}
              </p>
            </div>
          </div>
          
          {/* Linked Delay Event */}
          {timeImpact?.delay_event && (
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <p className="text-xs text-slate-500 uppercase mb-1">Linked Delay Event</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{timeImpact.delay_event.event_type}</p>
                  <p className="text-sm text-slate-600 line-clamp-1">{timeImpact.delay_event.narrative}</p>
                </div>
                <Badge className={
                  timeImpact.delay_event.still_impacting_programme 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-green-100 text-green-700'
                }>
                  {timeImpact.delay_event.still_impacting_programme ? 'Ongoing' : 'Resolved'}
                </Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Linked Items */}
      <Card className="rounded-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Linked Items
            {linkedItems.length > 0 && (
              <Badge variant="secondary">{linkedItems.length}</Badge>
            )}
          </CardTitle>
          <Button size="sm" onClick={() => { setLinkDialogOpen(true); setLinkableItems([]); }}>
            <Plus className="h-4 w-4 mr-1" />
            Link Item
          </Button>
        </CardHeader>
        <CardContent>
          {linkedItems.length > 0 ? (
            <div className="space-y-3">
              {linkedItems.map((item) => (
                <div key={item.item_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Badge className="text-xs capitalize">
                      {item.item_type?.replace('_', ' ')}
                    </Badge>
                    <div>
                      <p className="font-mono font-medium">{item.item_reference}</p>
                      {item.item_title && (
                        <p className="text-sm text-slate-600 line-clamp-1">{item.item_title}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.item_status && (
                      <Badge variant="outline" className="text-xs">{item.item_status}</Badge>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-600"
                      onClick={() => handleUnlinkItem(item.item_id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Paperclip className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No linked items</p>
              <p className="text-sm">Link RFIs, Delay Events, Site Instructions, etc.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link Item Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Item to Variation Order</DialogTitle>
            <DialogDescription>Select an item from another module to link</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Item Type Selector */}
            <div className="flex gap-2 flex-wrap">
              {ITEM_TYPES.map((type) => (
                <Button
                  key={type.value}
                  size="sm"
                  variant={selectedItemType === type.value ? "default" : "outline"}
                  onClick={() => { setSelectedItemType(type.value); setSearchQuery(''); }}
                >
                  {type.label}
                </Button>
              ))}
            </div>
            
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder={`Search ${selectedItemType.replace('_', ' ')}s...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchLinkable()}
              />
              <Button onClick={handleSearchLinkable}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Results */}
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {linkableItems.length > 0 ? (
                linkableItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                  >
                    <div>
                      <p className="font-mono font-medium">{item.reference}</p>
                      {item.title && (
                        <p className="text-sm text-slate-600 line-clamp-1">{item.title}</p>
                      )}
                      <p className="text-xs text-slate-500">
                        {item.status} • {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => handleLinkItem(item.id)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>No items found</p>
                  <p className="text-sm">Try a different search or item type</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Impact Dialog */}
      <Dialog open={timeImpactDialogOpen} onOpenChange={setTimeImpactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Time Impact</DialogTitle>
            <DialogDescription>Set the programme/time impact for this VO</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Impact Type</Label>
              <Select 
                value={timeImpactForm.impact_type} 
                onValueChange={(v) => setTimeImpactForm({ ...timeImpactForm, impact_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="Affects Critical Path">Affects Critical Path</SelectItem>
                  <SelectItem value="Non-Critical">Non-Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Claimed Days</Label>
                <Input
                  type="number"
                  value={timeImpactForm.claimed_days}
                  onChange={(e) => setTimeImpactForm({ ...timeImpactForm, claimed_days: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Assessed Days</Label>
                <Input
                  type="number"
                  value={timeImpactForm.assessed_days}
                  onChange={(e) => setTimeImpactForm({ ...timeImpactForm, assessed_days: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Approved Days</Label>
                <Input
                  type="number"
                  value={timeImpactForm.approved_days}
                  onChange={(e) => setTimeImpactForm({ ...timeImpactForm, approved_days: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={timeImpactForm.notes}
                onChange={(e) => setTimeImpactForm({ ...timeImpactForm, notes: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="Time impact notes and justification..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeImpactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTimeImpact}>
              <Save className="h-4 w-4 mr-2" />
              Save Time Impact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Compliance Tab Component
function ComplianceTab({ vo, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [timers, setTimers] = useState(null);
  const [approvalReqs, setApprovalReqs] = useState(null);
  const [showCreateTimer, setShowCreateTimer] = useState(false);
  const [newTimer, setNewTimer] = useState({
    timer_type: 'rfq_response',
    allowed_days: 14,
    current_status: '',
    target_status: ''
  });
  const [creating, setCreating] = useState(false);

  const loadComplianceData = useCallback(async () => {
    if (!vo?.id) return;
    setLoading(true);
    try {
      const [timersRes, approvalRes] = await Promise.all([
        variationOrdersApi.getComplianceTimers(vo.id),
        variationOrdersApi.getApprovalRequirements(vo.id)
      ]);
      setTimers(timersRes.data);
      setApprovalReqs(approvalRes.data);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
    } finally {
      setLoading(false);
    }
  }, [vo?.id]);

  useEffect(() => {
    loadComplianceData();
  }, [loadComplianceData]);

  const handleCreateTimer = async () => {
    if (!newTimer.timer_type || !newTimer.allowed_days) return;
    setCreating(true);
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + parseInt(newTimer.allowed_days));
      
      await variationOrdersApi.createComplianceTimer(vo.id, {
        timer_type: newTimer.timer_type,
        due_date: dueDate.toISOString(),
        allowed_days: parseInt(newTimer.allowed_days),
        current_status: vo.status,
        target_status: newTimer.target_status || null
      });
      toast.success('Compliance timer created');
      setShowCreateTimer(false);
      setNewTimer({ timer_type: 'rfq_response', allowed_days: 14, current_status: '', target_status: '' });
      loadComplianceData();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timer');
    } finally {
      setCreating(false);
    }
  };

  const handleCompleteTimer = async (timerId) => {
    try {
      await variationOrdersApi.completeComplianceTimer(vo.id, timerId);
      toast.success('Timer completed');
      loadComplianceData();
    } catch (error) {
      toast.error('Failed to complete timer');
    }
  };

  const handleEscalateTimer = async (timerId) => {
    const escalateTo = prompt('Escalate to (enter user ID or role):');
    if (!escalateTo) return;
    
    try {
      await variationOrdersApi.escalateComplianceTimer(vo.id, timerId, escalateTo);
      toast.success('Timer escalated');
      loadComplianceData();
    } catch (error) {
      toast.error('Failed to escalate timer');
    }
  };

  const getTimerStatusBadge = (status) => {
    const styles = {
      'On Track': 'bg-green-100 text-green-700',
      'Warning': 'bg-amber-100 text-amber-700',
      'Overdue': 'bg-red-100 text-red-700',
      'Escalated': 'bg-purple-100 text-purple-700',
      'Completed': 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const timerTypeLabels = {
    'rfq_response': 'RFQ Response',
    'quotation_review': 'Quotation Review',
    'technical_approval': 'Technical Approval',
    'commercial_approval': 'Commercial Approval',
    'proceed_approval': 'Proceed Approval',
    'implementation_start': 'Implementation Start'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Approval Requirements Card */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Approval Requirements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {approvalReqs ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">VO Value</p>
                  <p className="text-xl font-bold">{formatCurrency(approvalReqs.vo_value)}</p>
                </div>
                <Badge className="text-lg px-4 py-1">{approvalReqs.approval_level}</Badge>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg border ${approvalReqs.requirements?.technical?.completed ? 'bg-green-50 border-green-200' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Technical</span>
                    {approvalReqs.requirements?.technical?.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : approvalReqs.requirements?.technical?.required ? (
                      <Clock className="h-5 w-5 text-amber-500" />
                    ) : (
                      <span className="text-xs text-slate-400">Not Required</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {approvalReqs.requirements?.technical?.required ? 'Required' : 'Optional'}
                  </p>
                </div>
                
                <div className={`p-4 rounded-lg border ${approvalReqs.requirements?.commercial?.completed ? 'bg-green-50 border-green-200' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Commercial</span>
                    {approvalReqs.requirements?.commercial?.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : approvalReqs.requirements?.commercial?.required ? (
                      <Clock className="h-5 w-5 text-amber-500" />
                    ) : (
                      <span className="text-xs text-slate-400">Not Required</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {approvalReqs.requirements?.commercial?.required ? 'Required' : 'Optional'}
                  </p>
                </div>
                
                <div className={`p-4 rounded-lg border ${approvalReqs.requirements?.proceed?.completed ? 'bg-green-50 border-green-200' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Proceed</span>
                    {approvalReqs.requirements?.proceed?.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : approvalReqs.requirements?.proceed?.required ? (
                      <Clock className="h-5 w-5 text-amber-500" />
                    ) : (
                      <span className="text-xs text-slate-400">Not Required</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {approvalReqs.requirements?.proceed?.required ? 'Required' : 'Optional'}
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Can Proceed to Implementation?</span>
                  {approvalReqs.can_proceed ? (
                    <Badge className="bg-green-100 text-green-700">Yes</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">Pending Approvals</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Required approvers: {approvalReqs.required_approvers?.join(', ') || 'N/A'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No approval requirements found</p>
          )}
        </CardContent>
      </Card>

      {/* Compliance Timers Card */}
      <Card className="rounded-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Compliance Timers
          </CardTitle>
          <Button size="sm" onClick={() => setShowCreateTimer(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Timer
          </Button>
        </CardHeader>
        <CardContent>
          {timers?.summary && (
            <div className="grid grid-cols-5 gap-3 mb-6">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold">{timers.summary.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{timers.summary.on_track}</p>
                <p className="text-xs text-green-600">On Track</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-700">{timers.summary.warning}</p>
                <p className="text-xs text-amber-600">Warning</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">{timers.summary.overdue}</p>
                <p className="text-xs text-red-600">Overdue</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-700">{timers.summary.escalated}</p>
                <p className="text-xs text-purple-600">Escalated</p>
              </div>
            </div>
          )}

          {/* Active Timers */}
          {timers?.active_timers?.length > 0 ? (
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-slate-500 uppercase">Active Timers</h4>
              {timers.active_timers.map((timer) => (
                <div key={timer.id} className="p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{timerTypeLabels[timer.timer_type] || timer.timer_type}</span>
                      <Badge className={getTimerStatusBadge(timer.status)}>{timer.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleCompleteTimer(timer.id)}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Complete
                      </Button>
                      {timer.status === 'Overdue' && (
                        <Button size="sm" variant="destructive" onClick={() => handleEscalateTimer(timer.id)}>
                          <AlertTriangle className="h-4 w-4 mr-1" /> Escalate
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Due Date</p>
                      <p className="font-mono">{new Date(timer.due_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Days Remaining</p>
                      <p className={`font-bold ${timer.days_remaining < 0 ? 'text-red-600' : timer.days_remaining <= 2 ? 'text-amber-600' : 'text-green-600'}`}>
                        {timer.days_remaining < 0 ? `${Math.abs(timer.days_remaining)} overdue` : `${timer.days_remaining} days`}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Current Status</p>
                      <p>{timer.current_status}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Target Status</p>
                      <p>{timer.target_status || '-'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Clock className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No active compliance timers</p>
              <p className="text-xs mt-1">Timers are created automatically when VO status changes</p>
            </div>
          )}

          {/* Completed Timers */}
          {timers?.completed_timers?.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-sm text-slate-500 uppercase mb-3">Completed Timers</h4>
              <div className="space-y-2">
                {timers.completed_timers.slice(0, 5).map((timer) => (
                  <div key={timer.id} className="flex items-center justify-between p-3 bg-slate-50 rounded text-sm">
                    <span>{timerTypeLabels[timer.timer_type] || timer.timer_type}</span>
                    <span className="text-slate-500">Completed {timer.completed_at ? new Date(timer.completed_at).toLocaleDateString() : '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Timer Dialog */}
      <Dialog open={showCreateTimer} onOpenChange={setShowCreateTimer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Compliance Timer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Timer Type</Label>
              <Select value={newTimer.timer_type} onValueChange={(v) => setNewTimer({ ...newTimer, timer_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rfq_response">RFQ Response</SelectItem>
                  <SelectItem value="quotation_review">Quotation Review</SelectItem>
                  <SelectItem value="technical_approval">Technical Approval</SelectItem>
                  <SelectItem value="commercial_approval">Commercial Approval</SelectItem>
                  <SelectItem value="proceed_approval">Proceed Approval</SelectItem>
                  <SelectItem value="implementation_start">Implementation Start</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Allowed Days</Label>
              <Input
                type="number"
                value={newTimer.allowed_days}
                onChange={(e) => setNewTimer({ ...newTimer, allowed_days: e.target.value })}
                min={1}
              />
            </div>
            <div>
              <Label>Target Status (Optional)</Label>
              <Input
                value={newTimer.target_status}
                onChange={(e) => setNewTimer({ ...newTimer, target_status: e.target.value })}
                placeholder="e.g., Quote Received"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTimer(false)}>Cancel</Button>
            <Button onClick={handleCreateTimer} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create Timer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function VODetail() {
  const { voId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = usePermissions();
  
  const [vo, setVO] = useState(null);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Dialogs
  const [lineItemDialogOpen, setLineItemDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rfqDialogOpen, setRfqDialogOpen] = useState(false);
  const [recommendDialogOpen, setRecommendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Line item form
  const [lineItemForm, setLineItemForm] = useState({
    description: '',
    unit: '',
    quantity: '',
    rate: '',
    amount: '',
    pricing_method: 'Quotation (Lump Sum)',
    cost_code: '',
    notes: '',
  });
  const [editingLineItem, setEditingLineItem] = useState(null);
  
  // Approval form
  const [approvalForm, setApprovalForm] = useState({
    type: 'technical',
    status: 'approved',
    notes: '',
    conditions: '',
  });
  
  // RFQ form
  const [rfqForm, setRfqForm] = useState({
    issued_to: '',
    notes: '',
  });
  
  // Recommend form
  const [recommendForm, setRecommendForm] = useState({
    assessed_total: '',
    notes: '',
  });

  // Load VO
  const loadVO = useCallback(async () => {
    setLoading(true);
    try {
      const [voRes, enumsRes] = await Promise.all([
        variationOrdersApi.getById(voId),
        variationOrdersApi.getEnums()
      ]);
      setVO(voRes.data);
      setEnums(enumsRes.data);
      setEditData(voRes.data);
    } catch (error) {
      console.error('Failed to load VO:', error);
      toast.error('Failed to load Variation Order');
      navigate('/variation-orders');
    } finally {
      setLoading(false);
    }
  }, [voId, navigate]);

  useEffect(() => {
    loadVO();
  }, [loadVO]);

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      await variationOrdersApi.update(voId, editData);
      toast.success('Variation Order updated');
      setEditing(false);
      loadVO();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Delete VO
  const handleDeleteVO = async () => {
    setDeleting(true);
    try {
      await variationOrdersApi.delete(voId);
      toast.success('Variation Order deleted');
      navigate('/variation-orders');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete Variation Order');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Add/Edit line item
  const handleSaveLineItem = async () => {
    try {
      const item = {
        ...lineItemForm,
        quantity: lineItemForm.quantity ? parseFloat(lineItemForm.quantity) : null,
        rate: lineItemForm.rate ? parseFloat(lineItemForm.rate) : null,
        amount: lineItemForm.amount ? parseFloat(lineItemForm.amount) : null,
      };
      
      if (editingLineItem) {
        await variationOrdersApi.updateLineItem(voId, editingLineItem, item);
        toast.success('Line item updated');
      } else {
        await variationOrdersApi.addLineItem(voId, item);
        toast.success('Line item added');
      }
      
      setLineItemDialogOpen(false);
      setLineItemForm({
        description: '', unit: '', quantity: '', rate: '', amount: '',
        pricing_method: 'Quotation (Lump Sum)', cost_code: '', notes: '',
      });
      setEditingLineItem(null);
      loadVO();
    } catch (error) {
      toast.error('Failed to save line item');
    }
  };

  // Delete line item
  const handleDeleteLineItem = async (itemId) => {
    if (!confirm('Delete this line item?')) return;
    try {
      await variationOrdersApi.deleteLineItem(voId, itemId);
      toast.success('Line item deleted');
      loadVO();
    } catch (error) {
      toast.error('Failed to delete line item');
    }
  };

  // Add approval
  const handleAddApproval = async () => {
    try {
      await variationOrdersApi.addApproval(
        voId, 
        approvalForm.type, 
        approvalForm.status,
        approvalForm.notes,
        approvalForm.conditions
      );
      toast.success('Approval recorded');
      setApprovalDialogOpen(false);
      setApprovalForm({ type: 'technical', status: 'approved', notes: '', conditions: '' });
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add approval');
    }
  };

  // Issue RFQ
  const handleIssueRfq = async () => {
    try {
      await variationOrdersApi.issueRfq(voId, rfqForm.issued_to, rfqForm.notes);
      toast.success('RFQ issued');
      setRfqDialogOpen(false);
      setRfqForm({ issued_to: '', notes: '' });
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue RFQ');
    }
  };

  // Submit for review
  const handleSubmitForReview = async () => {
    try {
      await variationOrdersApi.submitForReview(voId);
      toast.success('Submitted for review');
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for review');
    }
  };

  // Recommend
  const handleRecommend = async () => {
    try {
      await variationOrdersApi.recommend(
        voId, 
        parseFloat(recommendForm.assessed_total),
        recommendForm.notes
      );
      toast.success('VO recommended for approval');
      setRecommendDialogOpen(false);
      setRecommendForm({ assessed_total: '', notes: '' });
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recommend');
    }
  };

  // Start work
  const handleStartWork = async () => {
    try {
      await variationOrdersApi.startWork(voId);
      toast.success('Work started');
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start work');
    }
  };

  // Mark implemented
  const handleMarkImplemented = async () => {
    try {
      await variationOrdersApi.markImplemented(voId);
      toast.success('Marked as implemented');
      loadVO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as implemented');
    }
  };

  // Calculate line items total
  const lineItemsTotal = vo?.line_items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!vo) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold">Variation Order not found</h3>
          <Button onClick={() => navigate('/variation-orders')} className="mt-4">
            Back to VO Register
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="vo-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="min-w-0 w-full sm:w-auto">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/variation-orders')}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to VO Register
            </Button>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="text-xl sm:text-3xl font-barlow font-bold uppercase tracking-tight">
                {vo.vo_number}
              </h1>
              <Badge className={`${getStatusBadge(vo.status)} border text-xs sm:text-sm`}>
                {vo.status}
              </Badge>
              {vo.proceed_at_risk && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  At Risk
                </Badge>
              )}
            </div>
            <p className="text-sm sm:text-base text-slate-600 mt-1 truncate">{vo.project_name}</p>
          </div>
          
          <div className="flex gap-2 flex-wrap shrink-0">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); setEditData(vo); }}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </>
            ) : (
              <>
                {permissions.can('variation_orders', 'edit') && (
                <Button variant="outline" onClick={() => setEditing(true)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                )}
                {permissions.can('variation_orders', 'delete') && (
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="details" className="w-full">
          {/* Mobile: Dropdown */}
          <div className="sm:hidden mb-4">
            <Select defaultValue="details" onValueChange={(value) => {
              const tabElement = document.querySelector(`[data-state="active"][value="${value}"]`);
              if (tabElement) tabElement.click();
            }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select tab" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="details">Details</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="rfqs">RFQs & Quotes</SelectItem>
                <SelectItem value="payments">Payments</SelectItem>
                <SelectItem value="linked">Linked Items</SelectItem>
                <SelectItem value="approvals">Approvals</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="activity">Activity Log</SelectItem>
                <SelectItem value="correspondence">Correspondence</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Tab List */}
          <TabsList className="mb-4 hidden sm:inline-flex">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="commercial">Commercial</TabsTrigger>
            <TabsTrigger value="rfqs">RFQs & Quotes</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="linked">Linked Items</TabsTrigger>
            <TabsTrigger value="approvals">Approvals</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="activity">Activity Log</TabsTrigger>
            <TabsTrigger value="correspondence">Correspondence</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
              {/* Main Info */}
              <Card className="xl:col-span-2 rounded-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Scope & Description</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editing ? (
                    <>
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={editData.title || ''}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={editData.description || ''}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Reason / Trigger</Label>
                        <Input
                          value={editData.reason || ''}
                          onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <h3 className="font-semibold text-lg">{vo.title}</h3>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 uppercase">Description</Label>
                        <p className="text-slate-700 mt-1 whitespace-pre-wrap">{vo.description || '-'}</p>
                      </div>
                      {vo.reason && (
                        <div>
                          <Label className="text-xs text-slate-500 uppercase">Reason / Trigger</Label>
                          <p className="text-slate-700 mt-1">{vo.reason}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Sidebar Info */}
              <div className="space-y-4">
                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Classification</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Category</span>
                      <span className="font-medium">{vo.change_category}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Contract Type</span>
                      <Badge variant="outline">{vo.contract_type}</Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Priority</span>
                      <Badge className={
                        vo.priority === 'Emergency' ? 'bg-red-100 text-red-700' :
                        vo.priority === 'Urgent' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {vo.priority}
                      </Badge>
                    </div>
                    {vo.disciplines?.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <span className="text-sm text-slate-500">Disciplines</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vo.disciplines.map((d) => (
                              <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Commercial Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Line Items Total</span>
                      <span className="font-mono font-medium">{formatCurrency(lineItemsTotal)}</span>
                    </div>
                    {vo.quoted_total && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-500">Quoted Total</span>
                          <span className="font-mono font-medium text-amber-600">{formatCurrency(vo.quoted_total)}</span>
                        </div>
                      </>
                    )}
                    {vo.assessed_total && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-500">Assessed Total</span>
                          <span className="font-mono font-medium text-blue-600">{formatCurrency(vo.assessed_total)}</span>
                        </div>
                      </>
                    )}
                    {vo.approved_total && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-500">Approved Total</span>
                          <span className="font-mono font-bold text-green-600">{formatCurrency(vo.approved_total)}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Created By</span>
                      <span>{vo.created_by_name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Created</span>
                      <span className="font-mono">{new Date(vo.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Updated</span>
                      <span className="font-mono">{new Date(vo.updated_at).toLocaleDateString()}</span>
                    </div>
                    {vo.issued_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Issued</span>
                        <span className="font-mono">{new Date(vo.issued_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Commercial Tab */}
          <TabsContent value="commercial">
            <Card className="rounded-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Line Items</CardTitle>
                <Button size="sm" onClick={() => {
                  setLineItemForm({
                    description: '', unit: '', quantity: '', rate: '', amount: '',
                    pricing_method: 'Quotation (Lump Sum)', cost_code: '', notes: '',
                  });
                  setEditingLineItem(null);
                  setLineItemDialogOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                {vo.line_items?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3">Description</th>
                          <th className="text-left p-3">Method</th>
                          <th className="text-right p-3">Qty</th>
                          <th className="text-right p-3">Rate</th>
                          <th className="text-right p-3">Amount</th>
                          <th className="text-right p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vo.line_items.map((item) => (
                          <tr key={item.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{item.description}</p>
                                {item.cost_code && (
                                  <p className="text-xs text-slate-500">Code: {item.cost_code}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <Badge variant="secondary" className="text-xs">{item.pricing_method}</Badge>
                            </td>
                            <td className="p-3 text-right font-mono">
                              {item.quantity} {item.unit}
                            </td>
                            <td className="p-3 text-right font-mono">
                              {item.rate ? formatCurrency(item.rate) : '-'}
                            </td>
                            <td className="p-3 text-right font-mono font-medium">
                              {formatCurrency(item.amount)}
                            </td>
                            <td className="p-3 text-right">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setLineItemForm({
                                    description: item.description,
                                    unit: item.unit || '',
                                    quantity: item.quantity?.toString() || '',
                                    rate: item.rate?.toString() || '',
                                    amount: item.amount?.toString() || '',
                                    pricing_method: item.pricing_method,
                                    cost_code: item.cost_code || '',
                                    notes: item.notes || '',
                                  });
                                  setEditingLineItem(item.id);
                                  setLineItemDialogOpen(true);
                                }}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleDeleteLineItem(item.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-bold">
                          <td colSpan="4" className="p-3 text-right">Total:</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(lineItemsTotal)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No line items yet</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => setLineItemDialogOpen(true)}
                    >
                      Add First Item
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RFQs & Quotations Tab */}
          <TabsContent value="rfqs">
            <RFQsAndQuotationsTab voId={voId} vo={vo} onRefresh={loadVO} formatCurrency={formatCurrency} />
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentsTab voId={voId} vo={vo} onRefresh={loadVO} formatCurrency={formatCurrency} />
          </TabsContent>

          {/* Linked Items Tab */}
          <TabsContent value="linked">
            <LinkedItemsTab voId={voId} vo={vo} onRefresh={loadVO} />
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals">
            <Card className="rounded-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Approval Trail</CardTitle>
                {['Recommended', 'Technically Approved', 'Commercially Approved'].includes(vo.status) && (
                  <Button size="sm" onClick={() => setApprovalDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Approval
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {vo.approvals?.length > 0 ? (
                  <div className="space-y-4">
                    {vo.approvals.map((approval) => (
                      <div key={approval.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={
                            approval.status === 'approved' ? 'bg-green-100 text-green-700' :
                            approval.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {approval.approval_type.toUpperCase()} - {approval.status.toUpperCase()}
                          </Badge>
                          <span className="text-sm text-slate-500 font-mono">
                            {new Date(approval.approved_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">
                          <span className="text-slate-500">By:</span> {approval.approved_by_name}
                        </p>
                        {approval.notes && (
                          <p className="text-sm mt-2 text-slate-600">{approval.notes}</p>
                        )}
                        {approval.conditions && (
                          <p className="text-sm mt-2 text-amber-600">
                            <strong>Conditions:</strong> {approval.conditions}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No approvals yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <ComplianceTab vo={vo} onUpdate={loadVO} />
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {vo.activity_log?.length > 0 ? (
                  <div className="space-y-3">
                    {[...vo.activity_log].reverse().map((log) => (
                      <div key={log.id} className="flex gap-3 text-sm border-l-2 border-slate-200 pl-4 py-2">
                        <History className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-slate-700">{log.details || log.action}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {log.performed_by_name} • {new Date(log.performed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <History className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No activity recorded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Correspondence Tab */}
          <TabsContent value="correspondence">
            <CorrespondenceTab 
              projectId={vo?.project_id}
              moduleType="vo"
              moduleEntityId={voId}
            />
          </TabsContent>
        </Tabs>

        {/* Line Item Dialog */}
        <Dialog open={lineItemDialogOpen} onOpenChange={setLineItemDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingLineItem ? 'Edit Line Item' : 'Add Line Item'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={lineItemForm.description}
                  onChange={(e) => setLineItemForm({ ...lineItemForm, description: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label>Pricing Method</Label>
                <Select 
                  value={lineItemForm.pricing_method}
                  onValueChange={(v) => setLineItemForm({ ...lineItemForm, pricing_method: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enums?.pricing_methods?.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={lineItemForm.quantity}
                    onChange={(e) => setLineItemForm({ ...lineItemForm, quantity: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={lineItemForm.unit}
                    onChange={(e) => setLineItemForm({ ...lineItemForm, unit: e.target.value })}
                    placeholder="m², nr, etc."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Rate</Label>
                  <Input
                    type="number"
                    value={lineItemForm.rate}
                    onChange={(e) => setLineItemForm({ ...lineItemForm, rate: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Amount (or calculated from Qty × Rate)</Label>
                <Input
                  type="number"
                  value={lineItemForm.amount}
                  onChange={(e) => setLineItemForm({ ...lineItemForm, amount: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Cost Code</Label>
                <Input
                  value={lineItemForm.cost_code}
                  onChange={(e) => setLineItemForm({ ...lineItemForm, cost_code: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLineItemDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveLineItem}>
                {editingLineItem ? 'Update' : 'Add'} Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Approval</DialogTitle>
              <DialogDescription>Record a technical, commercial, or proceed approval</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Approval Type</Label>
                <Select 
                  value={approvalForm.type}
                  onValueChange={(v) => setApprovalForm({ ...approvalForm, type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="proceed">Proceed / Issue VO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={approvalForm.status}
                  onValueChange={(v) => setApprovalForm({ ...approvalForm, status: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="conditional">Conditional</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {approvalForm.status === 'conditional' && (
                <div>
                  <Label>Conditions</Label>
                  <Textarea
                    value={approvalForm.conditions}
                    onChange={(e) => setApprovalForm({ ...approvalForm, conditions: e.target.value })}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={approvalForm.notes}
                  onChange={(e) => setApprovalForm({ ...approvalForm, notes: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddApproval}>Record Approval</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* RFQ Dialog */}
        <Dialog open={rfqDialogOpen} onOpenChange={setRfqDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue RFQ</DialogTitle>
              <DialogDescription>Request a quotation from the contractor</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Issue To *</Label>
                <Input
                  value={rfqForm.issued_to}
                  onChange={(e) => setRfqForm({ ...rfqForm, issued_to: e.target.value })}
                  placeholder="Contractor name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={rfqForm.notes}
                  onChange={(e) => setRfqForm({ ...rfqForm, notes: e.target.value })}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRfqDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleIssueRfq} disabled={!rfqForm.issued_to}>
                <Send className="h-4 w-4 mr-2" />
                Issue RFQ
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recommend Dialog */}
        <Dialog open={recommendDialogOpen} onOpenChange={setRecommendDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recommend for Approval</DialogTitle>
              <DialogDescription>QS recommendation with assessed value</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Assessed Total *</Label>
                <Input
                  type="number"
                  value={recommendForm.assessed_total}
                  onChange={(e) => setRecommendForm({ ...recommendForm, assessed_total: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={recommendForm.notes}
                  onChange={(e) => setRecommendForm({ ...recommendForm, notes: e.target.value })}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRecommendDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRecommend} disabled={!recommendForm.assessed_total}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Recommend
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Variation Order</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {vo?.vo_number}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteVO} 
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
